import { useState, useEffect, useCallback, useRef, MouseEvent } from 'react';
import {
  Alert,
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  IconButton,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useColorModeValue,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { pxToRem } from '@duke-hq/ui/constants/scalable';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { LoginUserType, useAuthTenant } from '@duke-hq/app-provider-web/tenant/useAuthTenant';
import { trpcTenant } from './tenant/trpcTenant';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { safeRedirectParam } from '@duke-hq/libauth-client/safeRedirectParam';
import { djs } from '@duke-hq/djs';
import { TRPCClientError } from '@trpc/client';
import { OTPInput } from './components/OTP';
import { Logo } from '@duke-hq/ui/components/Logo/Logo';
import { LogoFooter } from '@duke-hq/ui/components/LogoFooter/LogoFooter';
import { FieldValues, useForm } from 'react-hook-form';
import { isPhoneNumber } from './lib/helpers';
import MSSigninLight from '@duke-hq/ui/components/SSO/MSSigninLight';
import { TOO_MANY_ATTEMPTS_ERROR_MSG } from '@duke-hq/shared-constants/errors';

enum LoginTypeEnum {
  EMAIL = 'Email',
  PHONE = 'Phone',
}

let currentTokenRequest: string | null = null;

interface ResponseProps {
  date: Date;
  email?: string | null;
  phone?: string | null;
}

export function LoginPage() {
  const meQuery = trpcTenant.me.useQuery();
  const { user } = useAuthTenant();
  const navigate = useNavigate();
  const login = trpcTenant.auth.login.useMutation();

  const sso = trpcTenant.auth.getSSO.useQuery();
  const createPasswordlessLogin = trpcTenant.auth.createPasswordlessLogin.useMutation({
    onMutate: () => {
      setIsMagicLinkResend(false);
    },
  });

  const magicLinkLogin = trpcTenant.auth.magicLinkLogin.useMutation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [loginType, setLoginType] = useState(LoginTypeEnum.EMAIL);
  const [otpCode, setOtpCode] = useState('');
  const [email, setEmail] = useState('');
  const [expiredMagicLink, setExpiredMagicLink] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [challengeToken, setChallengeToken] = useState('');
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [resendResult, setResendResult] = useState<ResponseProps | null>(null);
  const [otpSentAt, setOtpSentAt] = useState<Date | null>(null);
  const [isVerifyButtonLoading, setIsVerifyButtonLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [isAttemptError, setIsAttemptError] = useState<string>();
  const [isMagicLinkResend, setIsMagicLinkResend] = useState(false);
  const [isShowEmailDomainHint, setIsShowEmailDomainHint] = useState(false);
  const [isHandleAzureOIDCLoading, setIsHandleAzureOIDCLoading] = useState(false);

  const {
    handleSubmit,
    register,
    formState: { errors },
    watch,
  } = useForm<{
    input: string;
  }>();
  const inputValue = watch('input', '');

  const bgColor = useColorModeValue('bg.muted', 'gray.800');

  useEffect(() => {
    if (user != null) {
      navigate('/');
    }
  }, [navigate, user]);

  const onSubmit = useCallback(
    async (data: FieldValues) => {
      const formData: Partial<LoginUserType> = {};
      const input = data?.input?.trim();

      setError('');
      setIsAttemptError('');

      try {
        if (typeof input === 'string' && input.includes('@')) {
          setEmail(input);
          setLoginType(LoginTypeEnum.EMAIL);
          formData.email = input;
        } else if (typeof input === 'string' && isPhoneNumber(input)) {
          setPhoneNumber(input);
          setLoginType(LoginTypeEnum.PHONE);
          formData.phone_number = input;
        } else {
          return;
        }

        const loginResponse = await createPasswordlessLogin.mutateAsync(formData);

        if (loginResponse?.email != null) {
          setEmail(loginResponse.email);
        }

        if (loginResponse?.challenge_token != null) {
          setChallengeToken(loginResponse.challenge_token);
        }

        setResendResult(null);
        setError('');
        setOtpCode('');
        onOpen();
      } catch (err) {
        if (err instanceof TRPCClientError && err.message === TOO_MANY_ATTEMPTS_ERROR_MSG) {
          setError(t`Too many attempts. Please try again later.`);
        } else {
          setError(t`An unexpected error occurred. Please try again later.`);
        }
      }
    },
    [createPasswordlessLogin, navigate, user, onOpen],
  );

  const handleOtpSubmit = useCallback(
    async (e?: MouseEvent<HTMLElement>, code?: string) => {
      e?.preventDefault();
      setIsVerifyButtonLoading(true);

      try {
        const sessionId = await login.mutateAsync({
          otp_code: code || otpCode,
          phone_number: phoneNumber,
          email,
          challenge_token: challengeToken,
        });

        if (sessionId) {
          // Redirect after successful login, using the return URL if one was
          // provided (e.g. when the user's session expired mid-use)
          window.location.href = safeRedirectParam(searchParams);
        }
      } catch (err) {
        if (err instanceof TRPCClientError && err.message === TOO_MANY_ATTEMPTS_ERROR_MSG) {
          setError(t`Too many attempts. Please try again later.`);
        } else {
          setError(t`The code you entered is incorrect or has expired. Please try again.`);
        }
      }

      setIsVerifyButtonLoading(false);
    },
    [login, setIsVerifyButtonLoading, challengeToken, email, otpCode, phoneNumber, searchParams],
  );

  const handleOtpOnComplete = useCallback(
    async (otp: string) => {
      setError('');
      setOtpCode(otp);
      await handleOtpSubmit(undefined, otp);
    },
    [handleOtpSubmit, setError, setOtpCode],
  );

  const handleOtpChange = useCallback(
    (code: string) => {
      setOtpCode(code);
      setError('');
    },
    [setOtpCode, setError],
  );

  const handleGoBack = useCallback(() => {
    setError('');
    setIsAttemptError('');
    onClose();
  }, [setError, setIsAttemptError, onClose]);

  const handlePasswordlessResend = useCallback(async () => {
    const result = await createPasswordlessLogin.mutateAsync({
      phoneNumber: phoneNumber,
      email,
    });

    if (result) {
      setResendResult(result);
      setOtpSentAt(new Date());
      setError('');
      setOtpCode('');
      if (result.challenge_token != null) {
        setChallengeToken(result.challenge_token);
      }
    }
  }, [
    createPasswordlessLogin,
    phoneNumber,
    email,

    setResendResult,
    setOtpSentAt,
    setError,
    setOtpCode,
  ]);

  const handleMagicLinkLogin = useCallback(
    async (token: string) => {
      currentTokenRequest = token;
      const tokenExpired = await magicLinkLogin.mutateAsync({
        token,
      });

      if (tokenExpired) {
        setExpiredMagicLink(true);
      } else {
        await meQuery.refetch();
        window.location.href = safeRedirectParam(searchParams);
      }
    },
    [magicLinkLogin, meQuery, searchParams],
  );

  const token = searchParams.get('token');
  const redirectFromApp = searchParams.get('redirectFromApp');

  useEffect(() => {
    if (token && location.pathname === '/magiclink') {
      if (token && !currentTokenRequest) {
        handleMagicLinkLogin(token);
      }
    }

    if (redirectFromApp) {
      setIsShowEmailDomainHint(true);
    }
  }, [token, location.pathname, redirectFromApp, handleMagicLinkLogin]);

  const { data: oidcAuthorizationUrl, refetch: oidcBuildAuthorizationUrl } =
    trpcTenant.oidc.buildAuthorizationUrl.useQuery(
      { provider: 'azure' },
      {
        enabled: false,
        retry: false,
      },
    );

  const handleAzureOIDC = useCallback(
    async (e?: MouseEvent<HTMLElement>) => {
      e?.preventDefault();

      setIsHandleAzureOIDCLoading(true);

      try {
        await oidcBuildAuthorizationUrl();
      } catch (err) {
        setError(t`SSO failed`);
      }

      setIsHandleAzureOIDCLoading(false);
    },
    [oidcBuildAuthorizationUrl],
  );

  const [oidcError, setOIDCError] = useState<string>();

  const authorizationCallback = trpcTenant.oidc.authorizationCallback.useMutation({
    onSuccess() {
      window.location.href = safeRedirectParam(searchParams);
    },
    onError: (error) => {
      setOIDCError(error.message);
    },
  });

  const isMounted = useRef(false);

  useEffect(() => {
    if (oidcAuthorizationUrl != null) {
      window.location.href = oidcAuthorizationUrl;
    }
  }, [oidcAuthorizationUrl]);

  useEffect(() => {
    const handleOIDCLOgin = async () => {
      await authorizationCallback.mutateAsync({
        uri: window.location.href,
      });
    };

    if (location.pathname === '/oidc/azure/callback' && !isMounted.current) {
      isMounted.current = true;
      void handleOIDCLOgin();
    }
  }, [authorizationCallback, location.pathname]);

  if (typeof expiredMagicLink === 'string' && typeof currentTokenRequest === 'string') {
    return (
      <Flex minH="100vh" align="center" justify="center" bg={bgColor} p="8">
        <Stack spacing={8} mx={'auto'} maxW={'lg'} py={12} px={6}>
          <Stack align={'center'}>
            {createPasswordlessLogin.data ? (
              <>
                <Stack
                  align="center"
                  width="full"
                  px="16"
                  py="8"
                  gap="8"
                  bg="bg.surface"
                  borderColor="border.emphasis"
                  borderRadius="xl"
                >
                  <Text
                    fontSize="2xl"
                    lineHeight="8"
                    fontWeight="semibold"
                    color="fg.default"
                    align="center"
                  >
                    <Trans>Join the learning revolution</Trans>
                  </Text>

                  <VStack width="full">
                    <Text fontSize="md" lineHeight="6" color="fg.muted" align="center">
                      <Trans>We sent an email to</Trans>&nbsp;
                      <Text fontWeight="semibold">{createPasswordlessLogin.data.email}</Text>
                    </Text>
                    <Text fontSize="sm" lineHeight="6" color="fg.muted" align="center">
                      <Trans>Sent {djs(createPasswordlessLogin.data.date).fromNow()}</Trans>
                    </Text>

                    <Button
                      variant="ghost"
                      onClick={() => {
                        setExpiredMagicLink(false);
                      }}
                    >
                      <Trans>Back home</Trans>
                    </Button>
                  </VStack>
                </Stack>

                <LogoFooter />
              </>
            ) : (
              <>
                <Heading fontSize="xl">
                  <Trans>Magic link expired</Trans>
                </Heading>
                <Button
                  isDisabled={isMagicLinkResend}
                  isLoading={isMagicLinkResend}
                  loadingText="Submitting"
                  onClick={async () => {
                    if (currentTokenRequest) {
                      setIsMagicLinkResend(true);
                      await createPasswordlessLogin.mutateAsync({
                        expiredMagicLinkToken: currentTokenRequest,
                      });
                    }
                  }}
                >
                  <Trans>Send a new magic link</Trans>
                </Button>
              </>
            )}
          </Stack>
        </Stack>
      </Flex>
    );
  }

  if (location.pathname === '/oidc/azure/callback' && oidcError === undefined) {
    return null;
  }

  return (
    <Flex minH="100vh" align="center" justify="center" bg={bgColor} p="8">
      <Stack mx="auto" width="auto" maxW={pxToRem(530)} gap={{ base: '6', md: '8' }} align="center">
        <Logo />

        <Stack
          align="center"
          width="full"
          minW={{ base: 'auto', md: pxToRem(530) }}
          px={{ base: '4', md: '16' }}
          py={{ base: '6', md: '8' }}
          gap={{ base: '4', md: '8' }}
          bg="bg.surface"
          borderColor="border.emphasis"
          borderRadius="xl"
        >
          <Text
            fontSize="2xl"
            lineHeight="8"
            fontWeight="semibold"
            color="fg.default"
            align="center"
          >
            <Trans>Login to your account</Trans>
          </Text>

          {isShowEmailDomainHint ? (
            <Alert status="warning" borderRadius="md" variant="left-accent">
              <Text fontSize="sm" lineHeight="5" color="fg.muted" align="left">
                <Trans>
                  It looks like you are trying to log in as a consultant. Please enter your email
                  address to continue. For future logins, you can bookmark this page for easy
                  access!
                </Trans>
              </Text>
            </Alert>
          ) : null}

          <form onSubmit={handleSubmit(onSubmit)} noValidate style={{ width: '100%' }}>
            <Stack w="full" align="center" gap={{ base: '4', md: '6' }}>
              <FormControl isInvalid={!!errors.input}>
                <FormLabel
                  htmlFor="input"
                  fontSize="sm"
                  lineHeight="5"
                  fontWeight="normal"
                  color="fg.emphasis"
                >
                  <Trans>Email</Trans>
                </FormLabel>
                <Input
                  id="input"
                  {...register('input', {
                    required: 'This is required',
                    validate: (value: string) => {
                      return (
                        value.includes('@') ||
                        isPhoneNumber(value) ||
                        'Please enter a valid phone number or email'
                      );
                    },
                  })}
                  spellCheck="false"
                  type="text"
                  isReadOnly={createPasswordlessLogin.isPending}
                />
                <FormErrorMessage>{errors.input?.message}</FormErrorMessage>
              </FormControl>

              {isAttemptError ? (
                <Text
                  fontSize="sm"
                  lineHeight="5"
                  fontWeight="medium"
                  color="danger"
                  align="center"
                  maxW="xs"
                >
                  {isAttemptError}
                </Text>
              ) : null}

              <Button
                type="submit"
                w="fit-content"
                fontSize="md"
                px="9"
                isDisabled={inputValue.trim() === '' || !!errors.input}
                isLoading={createPasswordlessLogin.isPending}
              >
                <Trans>Continue</Trans>
              </Button>

              {sso.data?.azureOIDC === true ? (
                <>
                  <Flex align="center">
                    <Divider />
                    <Text>
                      <Trans>OR</Trans>
                    </Text>
                    <Divider />
                  </Flex>
                  <IconButton
                    type="submit"
                    onClick={handleAzureOIDC}
                    isLoading={isHandleAzureOIDCLoading}
                    aria-label="MS_Sign_In"
                  >
                    <MSSigninLight />
                  </IconButton>
                  {oidcError != null && (
                    <Text fontSize="14" color="red.500" align="center">
                      <Trans>Error:</Trans> {oidcError}
                    </Text>
                  )}
                </>
              ) : null}

              <Flex
                flexDirection={{ base: 'column', md: 'row' }}
                gap={{ base: '1', md: '2' }}
                justify="center"
                align="center"
              >
                <Text fontSize="md" lineHeight="6" fontWeight="medium">
                  <Trans>New to Duke?</Trans>
                </Text>
                <Link href="/signup">Sign up</Link>
              </Flex>
            </Stack>
          </form>

          <Flex flexDirection="column" justify="center" align="center">
            <Text fontSize="sm" lineHeight="5" color="fg.muted" align="center">
              <Trans>Having trouble logging in?</Trans>
              <br />
              <Trans>Email</Trans>&nbsp;
              <Link href="mailto:support@duke.co">support@duke.co</Link>&nbsp;
              <Trans>or call</Trans>&nbsp;
              <Link href="tel:1300.Duke.Co">1300 Duke Co</Link>
            </Text>
          </Flex>
        </Stack>

        <LogoFooter />
      </Stack>

      <Modal closeOnOverlayClick={false} isOpen={isOpen} onClose={onClose} isCentered size="full">
        <ModalOverlay />
        <ModalContent bg="bg.muted">
          <ModalHeader></ModalHeader>
          <ModalCloseButton size="xs" />
          <ModalBody as={Flex} align="center" justify="center">
            <Stack
              flexDirection="column"
              mx="auto"
              height="full"
              width="auto"
              maxW={pxToRem(530)}
              gap={{ base: '6', md: '8' }}
              align="center"
            >
              <Logo />
              <Stack
                align="center"
                width="full"
                px={{ base: '4', md: '16' }}
                py={{ base: '6', md: '8' }}
                gap="6"
                bg="bg.surface"
                borderColor="border.emphasis"
                borderRadius="xl"
              >
                <VStack width="full" gap="4">
                  <Text
                    fontSize="2xl"
                    lineHeight="8"
                    fontWeight="semibold"
                    color="fg.default"
                    align="center"
                  >
                    <Trans>Enter your single-use</Trans>
                    <br />
                    <Trans>passcode</Trans>
                  </Text>
                  <Text fontSize="md" lineHeight="6" color="fg.muted" align="center">
                    <Trans>We sent</Trans>&nbsp;
                    {loginType === LoginTypeEnum.EMAIL ? (
                      <Trans>an email to</Trans>
                    ) : (
                      <Trans>a text message to</Trans>
                    )}
                    <br />
                    <span style={{ fontWeight: 'semibold' }}>
                      {loginType === LoginTypeEnum.EMAIL ? email : phoneNumber}
                    </span>
                  </Text>
                </VStack>

                <OTPInput onOtpInputChange={handleOtpChange} onSubmit={handleOtpOnComplete} />

                {error != null ? (
                  <Text
                    fontSize="sm"
                    lineHeight="5"
                    fontWeight="medium"
                    color="danger"
                    align="center"
                    maxW="xs"
                  >
                    {error}
                  </Text>
                ) : null}

                <Button
                  type="submit"
                  onClick={handleOtpSubmit}
                  isDisabled={isVerifyButtonLoading || otpCode.length < 4}
                  isLoading={isVerifyButtonLoading}
                  px="9"
                >
                  <Trans>Submit</Trans>
                </Button>

                <ResendResults
                  resendResult={resendResult}
                  loginType={loginType}
                  otpSentAt={otpSentAt}
                  createPasswordlessLogin={createPasswordlessLogin}
                  handlePasswordlessResend={handlePasswordlessResend}
                />
              </Stack>

              <Button variant="ghost" onClick={handleGoBack}>
                <Trans>Go back</Trans>
              </Button>

              <LogoFooter />
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
}

const ResendResults = ({
  resendResult,
  loginType,
  otpSentAt,
  createPasswordlessLogin,
  handlePasswordlessResend,
}) => {
  let content;

  if (resendResult) {
    content = (
      <Text fontSize="sm" lineHeight="5" color="fg.muted" align="center">
        <Trans>Sent second</Trans>&nbsp;
        {loginType === LoginTypeEnum.EMAIL ? <Trans>email</Trans> : <Trans>text message</Trans>}
        &nbsp;
        {djs(otpSentAt).fromNow()}
      </Text>
    );
  } else {
    let resendText;
    if (createPasswordlessLogin.isPending) {
      resendText =
        loginType === LoginTypeEnum.EMAIL ? (
          <Trans>resending the email</Trans>
        ) : (
          <Trans>resending the text message</Trans>
        );
    } else {
      resendText = (
        <Link onClick={handlePasswordlessResend}>
          {loginType === LoginTypeEnum.EMAIL ? (
            <Trans>resend the email</Trans>
          ) : (
            <Trans>resend the text message</Trans>
          )}
        </Link>
      );
    }

    content = (
      <Text fontSize="sm" lineHeight="5" color="fg.muted" align="center">
        <Trans>Didn't get the</Trans>&nbsp;
        {loginType === LoginTypeEnum.EMAIL ? <Trans>email?</Trans> : <Trans>text message?</Trans>}
        &nbsp;
        <Trans>Check your spam folder.</Trans>
        <br />
        <Trans>If you still can't find it,</Trans>&nbsp;
        {resendText}
      </Text>
    );
  }

  return content;
};
